import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, TrendingUp, Users, X } from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Scatter,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  ReferenceLine,
  Brush,
} from "recharts";
import { normalizePlayerPosition, PLAYER_POSITION_ORDER } from "../common/PlayerPositions";
import { isPlayerAtRisk } from "../common/playerRiskUtils";
import RpeTrendPlayerSelector from "./RpeTrendPlayerSelector";
import {
  getFeedbackActiveBar,
  getFeedbackBarCursor,
  getFeedbackLineCursor,
  getFeedbackStatCardClassName,
  getFeedbackTooltipStyle,
} from "./feedbackOverviewTheme";
import FeedbackDetailModal from "./FeedbackDetailModal";
import { buildPlayerProfileLookup, PlayerIdentityCell } from "./feedbackOverviewPlayer";
import FeedbackInsufficientDataPanel from "./FeedbackInsufficientDataPanel";
import RpeOverviewExportMenu from "./RpeOverviewExportMenu";
import RpeSessionManageBar from "./RpeSessionManageBar.jsx";
import RpeAutoCloseSettings from "./RpeAutoCloseSettings.jsx";
import {
  MIN_OVERVIEW_RESPONSES,
  countRpeAnswers,
  canShowOverview,
  getOverviewMinRequired,
  getReadyDatesFromRpeHistory,
} from "./feedbackOverviewThresholds";
import { currentRpeSportDayIso, RPE_TREND_HISTORY_DAYS } from "./rpeOverviewUtils.js";

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatDayLabel(isoDate) {
  if (!isoDate) return "";
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function average(values) {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function formatMetric(value, decimals = 1) {
  if (!Number.isFinite(value)) return "-";
  return Number(value).toFixed(decimals);
}

const TREND_DASHES = ["", "6 4", "3 3", "10 5 2 5"];

function buildTrendPalette(count, isDarkMode) {
  const saturation = 70;
  const lightPrimary = isDarkMode ? 65 : 45;
  const lightSecondary = isDarkMode ? 55 : 55;
  return Array.from({ length: count }, (_, index) => {
    const hue = (index * 137.508) % 360;
    const lightness = index % 2 ? lightPrimary : lightSecondary;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  });
}

function getPlayerDisplayName(player) {
  return `${player.first_name || ""} ${player.last_name || ""}`.trim() || String(player.id);
}

const StatCard = ({ isDarkMode, label, value, hint, onClick }) => {
  const clickable = typeof onClick === "function";

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!clickable) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      className={`rounded-xl border p-3 transition-colors duration-150 ${getFeedbackStatCardClassName(isDarkMode)} ${
        clickable ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400/60" : ""
      }`}
    >
      <p className={`text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {hint ? <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{hint}</p> : null}
    </div>
  );
};

const Panel = ({ isDarkMode, title, subtitle, children }) => (
  <div
    className={`rounded-2xl border p-4 ${
      isDarkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white"
    }`}
  >
    <div className="mb-3">
      <h4 className="text-sm font-semibold">{title}</h4>
      {subtitle ? (
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{subtitle}</p>
      ) : null}
    </div>
    {children}
  </div>
);

const TrendDetailsModeToggle = ({ isDarkMode, showDetails, onChange, t }) => (
  <div
    role="group"
    aria-label={t("feedback.rpe_trend_view_mode", { defaultValue: "Mode d'affichage de la tendance" })}
    className={`inline-flex rounded-xl border p-1 ${
      isDarkMode ? "border-gray-600/80 bg-gray-900/70" : "border-gray-200 bg-slate-100/90"
    }`}
  >
    <button
      type="button"
      aria-pressed={!showDetails}
      onClick={() => onChange(false)}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
        !showDetails
          ? isDarkMode
            ? "bg-amber-500/20 text-amber-100 shadow-sm ring-1 ring-amber-400/35"
            : "bg-white text-amber-800 shadow-sm ring-1 ring-amber-200/80"
          : isDarkMode
          ? "text-gray-400 hover:bg-gray-800/80 hover:text-gray-200"
          : "text-gray-500 hover:bg-white/70 hover:text-gray-700"
      }`}
    >
      <TrendingUp className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
      {t("feedback.rpe_trend_mode_average", { defaultValue: "Moyenne" })}
    </button>
    <button
      type="button"
      aria-pressed={showDetails}
      onClick={() => onChange(true)}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
        showDetails
          ? isDarkMode
            ? "bg-amber-500/20 text-amber-100 shadow-sm ring-1 ring-amber-400/35"
            : "bg-white text-amber-800 shadow-sm ring-1 ring-amber-200/80"
          : isDarkMode
          ? "text-gray-400 hover:bg-gray-800/80 hover:text-gray-200"
          : "text-gray-500 hover:bg-white/70 hover:text-gray-700"
      }`}
    >
      <Users className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
      {t("feedback.rpe_trend_show_details", { defaultValue: "Detail joueurs" })}
    </button>
  </div>
);

const getTrendSeriesTooltipMeta = (entry, { averageTrendLabel, coachTrendLabel, branchTrendLabel }) => {
  const key = entry.dataKey ?? entry.name;
  const payload = entry.payload || {};
  const value = entry.value;

  if (!Number.isFinite(value)) return null;

  if (key === "averageMain") {
    return {
      color: "#f59e0b",
      title: averageTrendLabel,
      sessionLabel: payload.sessionLabel,
      responsesCount: payload.responsesCount,
      value,
    };
  }
  if (key === "averageBranch") {
    return {
      color: "#fb923c",
      title: branchTrendLabel,
      sessionLabel: payload.sessionLabel,
      responsesCount: payload.responsesCount,
      value,
    };
  }
  if (key === "coachReferenceMain") {
    return {
      color: "#6366f1",
      title: coachTrendLabel,
      value,
    };
  }
  return {
    color: entry.color || entry.stroke,
    title: String(key),
    value,
  };
};

const RpeTrendTooltip = ({
  active,
  payload,
  label,
  isDarkMode,
  t,
  trendDayLabels,
  averageTrendLabel,
  coachTrendLabel,
  branchTrendLabel,
}) => {
  if (!active || !payload?.length) return null;

  const firstPayload = payload[0]?.payload;
  const dayIndex = firstPayload?.dayIndex;
  const dateLabel = firstPayload?.label ?? trendDayLabels[dayIndex] ?? label;

  const rows = payload
    .map((entry) =>
      getTrendSeriesTooltipMeta(entry, {
        averageTrendLabel,
        coachTrendLabel,
        branchTrendLabel,
      })
    )
    .filter(Boolean);

  if (!rows.length) return null;

  return (
    <div
      className={`max-w-xs rounded-lg border px-3 py-2 text-xs shadow-lg ${
        isDarkMode ? "border-gray-600 bg-gray-900 text-gray-100" : "border-gray-200 bg-white text-gray-900"
      }`}
    >
      <p className={`mb-2 font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>
        {t("feedback.rpe_trend_tooltip_date", { defaultValue: "Date" })}: {dateLabel}
      </p>
      <ul className="space-y-2">
        {rows.map((row, index) => (
          <li key={`${row.title}-${index}`}>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: row.color }} />
              <span className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{row.title}</span>
              <span className={`ml-auto font-semibold tabular-nums ${isDarkMode ? "text-amber-200" : "text-amber-700"}`}>
                {formatMetric(row.value)}
              </span>
            </div>
            {row.sessionLabel ? (
              <p className={`mt-0.5 pl-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {t("feedback.rpe_trend_tooltip_session", { defaultValue: "Session" })}: {row.sessionLabel}
              </p>
            ) : null}
            {Number.isFinite(row.responsesCount) ? (
              <p className={`pl-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                {t("feedback.rpe_trend_tooltip_responses", {
                  count: row.responsesCount,
                  defaultValue: "{{count}} réponses",
                })}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

const RpeTrendChart = ({
  trendChartData,
  trendBranchData,
  trendDayLabels,
  showTrendDetails,
  visibleTrendPlayers,
  getStyleForPlayer,
  isDarkMode,
  expanded = false,
  showBrush = false,
  t,
  averageTrendLabel,
  coachTrendLabel,
  branchTrendLabel,
  containerClassName = "h-64",
}) => {
  const lineCursor = getFeedbackLineCursor(isDarkMode);
  const mainDotR = expanded ? 6 : 4;
  const branchDotR = expanded ? 5 : 3;
  const coachDotR = expanded ? 4 : 2.5;
  const playerDotR = expanded ? 4 : 2.5;

  return (
    <div className={containerClassName}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={trendChartData}
          margin={expanded ? { top: 12, right: 24, left: 4, bottom: showBrush ? 28 : 8 } : { top: 4, right: 8, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
          <XAxis
            type="number"
            dataKey="dayIndex"
            domain={[0, Math.max(0, trendDayLabels.length - 1)]}
            ticks={trendDayLabels.map((_, index) => index)}
            tickFormatter={(index) => trendDayLabels[index] ?? ""}
            allowDecimals={false}
            tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: expanded ? 13 : 12 }}
          />
          <YAxis
            domain={[1, 10]}
            tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: expanded ? 13 : 12 }}
            width={expanded ? 36 : 30}
          />
          <Tooltip
            shared={false}
            cursor={lineCursor}
            content={
              <RpeTrendTooltip
                isDarkMode={isDarkMode}
                t={t}
                trendDayLabels={trendDayLabels}
                averageTrendLabel={averageTrendLabel}
                coachTrendLabel={coachTrendLabel}
                branchTrendLabel={branchTrendLabel}
              />
            }
          />
          <Line
            type="monotone"
            dataKey="averageMain"
            name={averageTrendLabel}
            stroke="#f59e0b"
            strokeWidth={expanded ? 3.25 : 2.75}
            dot={{ r: mainDotR, fill: "#f59e0b", stroke: "#fff", strokeWidth: 1 }}
            activeDot={{ r: mainDotR + 2 }}
            connectNulls
          />
          {trendBranchData.length > 0 && (
            <Scatter
              data={trendBranchData}
              dataKey="averageBranch"
              name={branchTrendLabel}
              fill="#fb923c"
              xAxisId={0}
              yAxisId={0}
              shape={(props) => {
                const { cx, cy } = props;
                if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
                return (
                  <circle cx={cx} cy={cy} r={branchDotR} fill="#fb923c" stroke="#fff" strokeWidth={expanded ? 1.5 : 1} />
                );
              }}
            />
          )}
          <Line
            type="monotone"
            dataKey="coachReferenceMain"
            name={coachTrendLabel}
            stroke="#6366f1"
            strokeWidth={expanded ? 2.5 : 2}
            strokeDasharray="6 4"
            dot={{ r: coachDotR, fill: "#6366f1" }}
            activeDot={{ r: coachDotR + 2 }}
            connectNulls={false}
          />
          {showTrendDetails &&
            visibleTrendPlayers.map((player) => {
              const { color, dash } = getStyleForPlayer(player.name);
              return (
                <Line
                  key={player.id}
                  type="monotone"
                  dataKey={player.name}
                  name={player.name}
                  stroke={color}
                  strokeWidth={expanded ? 2.2 : 1.8}
                  strokeDasharray={dash}
                  dot={{
                    r: playerDotR,
                    stroke: color,
                    fill: isDarkMode ? "#111827" : "#ffffff",
                  }}
                  activeDot={{ r: playerDotR + 2 }}
                  connectNulls={false}
                />
              );
            })}
          {showBrush && trendDayLabels.length > 1 ? (
            <Brush
              dataKey="dayIndex"
              height={22}
              stroke="#f59e0b"
              fill={isDarkMode ? "#1f2937" : "#fff7ed"}
              tickFormatter={(index) => trendDayLabels[index] ?? ""}
              travellerWidth={expanded ? 12 : 8}
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const RpeFeedbackOverview = ({
  rows = [],
  history = [],
  displayedPlayers = [],
  selectedPosition = "All",
  activeAlertPlayerIds = new Set(),
  coachRpeValue = null,
  selectedDate = null,
  onSelectDate,
  isDarkMode = false,
  historyLoading = false,
  onRequestEdit,
  onRequestBackfill,
  canEditInputs = true,
  sessions = [],
  selectedSessionId = null,
  onSelectSession,
  formatSessionLabel,
  clubId,
  onSessionMutated,
}) => {
  const { t } = useTranslation();
  const [detailModal, setDetailModal] = useState(null);
  const [trendExpanded, setTrendExpanded] = useState(false);
  const [showTrendDetails, setShowTrendDetails] = useState(false);
  const [activeTrendPosition, setActiveTrendPosition] = useState(null);
  const [selectedTrendPlayerIds, setSelectedTrendPlayerIds] = useState(() => new Set());
  const [showAllTrendByPosition, setShowAllTrendByPosition] = useState({});

  const allowedPlayerIds = useMemo(
    () => new Set((displayedPlayers || []).map((player) => String(player.id))),
    [displayedPlayers]
  );

  const rpeRowByPlayerId = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((row) => {
      const playerId = String(row?.player_id ?? "").trim();
      if (!playerId) return;
      map.set(playerId, row);
    });
    return map;
  }, [rows]);

  const filteredRows = useMemo(() => {
    return (displayedPlayers || [])
      .filter((player) => {
        if (selectedPosition !== "All" && normalizePlayerPosition(player.position) !== selectedPosition) {
          return false;
        }
        return allowedPlayerIds.has(String(player.id));
      })
      .map((player) => {
        const playerId = String(player.id);
        const row = rpeRowByPlayerId.get(playerId);
        if (row) {
          return {
            ...row,
            player_id: row.player_id ?? player.id,
            player_name: row.player_name || getPlayerDisplayName(player),
            player_position: row.player_position || player.position || "N/A",
            numericValue: toNumber(row.rpe_value),
            numericPrevValue: toNumber(row.prev_rpe_value),
          };
        }

        return {
          player_id: player.id,
          player_name: getPlayerDisplayName(player),
          player_position: player.position || "N/A",
          feedback_id: null,
          rpe_value: "N/A",
          prev_rpe_value: null,
          date: "N/A",
          numericValue: null,
          numericPrevValue: null,
        };
      });
  }, [displayedPlayers, allowedPlayerIds, selectedPosition, rpeRowByPlayerId]);

  const playerProfileById = useMemo(
    () => buildPlayerProfileLookup(displayedPlayers),
    [displayedPlayers]
  );

  const answeredRows = useMemo(
    () => filteredRows.filter((row) => Number.isFinite(row.numericValue)),
    [filteredRows]
  );

  const answerCount = answeredRows.length;
  const globalAnswerCount = useMemo(() => countRpeAnswers(rows), [rows]);
  const scopedPlayerCount = (displayedPlayers || []).length;
  const effectiveMinRequired = getOverviewMinRequired({
    minRequired: MIN_OVERVIEW_RESPONSES,
    scopedPlayerCount,
  });
  const hasOverviewData = canShowOverview({
    scopedAnswerCount: answerCount,
    globalAnswerCount,
    minRequired: MIN_OVERVIEW_RESPONSES,
    scopedPlayerCount,
  });

  const previousReadyDates = useMemo(
    () => getReadyDatesFromRpeHistory(history, allowedPlayerIds, MIN_OVERVIEW_RESPONSES, selectedDate),
    [history, allowedPlayerIds, selectedDate]
  );

  const values = useMemo(() => answeredRows.map((row) => row.numericValue), [answeredRows]);

  const averageValue = useMemo(() => average(values), [values]);
  const medianValue = useMemo(() => median(values), [values]);
  const numericCoachRpeValue = useMemo(() => toNumber(coachRpeValue), [coachRpeValue]);
  const coachVsAverageDelta = useMemo(() => {
    if (!Number.isFinite(numericCoachRpeValue) || !Number.isFinite(averageValue)) return null;
    return averageValue - numericCoachRpeValue;
  }, [numericCoachRpeValue, averageValue]);

  const minValue = useMemo(() => (values.length ? Math.min(...values) : null), [values]);
  const maxValue = useMemo(() => (values.length ? Math.max(...values) : null), [values]);

  const completionRate = useMemo(() => {
    if (!filteredRows.length) return 0;
    return (answeredRows.length / filteredRows.length) * 100;
  }, [answeredRows.length, filteredRows.length]);

  const deltaRows = useMemo(() => {
    return answeredRows
      .filter((row) => Number.isFinite(row.numericPrevValue))
      .map((row) => ({
        ...row,
        delta: row.numericValue - row.numericPrevValue,
      }));
  }, [answeredRows]);

  const averageDelta = useMemo(() => {
    if (!deltaRows.length) return null;
    return average(deltaRows.map((row) => row.delta));
  }, [deltaRows]);

  const biggestMovers = useMemo(() => {
    return [...deltaRows]
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 6);
  }, [deltaRows]);

  const distributionData = useMemo(() => {
    const bins = Array.from({ length: 10 }, (_, index) => ({
      bucket: String(index + 1),
      count: 0,
      rows: [],
    }));
    answeredRows.forEach((row) => {
      const bucket = Math.max(1, Math.min(10, Math.round(row.numericValue)));
      bins[bucket - 1].count += 1;
      bins[bucket - 1].rows.push(row);
    });
    return bins;
  }, [answeredRows]);

  const byPositionData = useMemo(() => {
    const positionMap = new Map();
    answeredRows.forEach((row) => {
      const position = normalizePlayerPosition(row.player_position) || "Unknown";
      const current = positionMap.get(position) || { position, values: [], rows: [] };
      current.values.push(row.numericValue);
      current.rows.push(row);
      positionMap.set(position, current);
    });

    return [...positionMap.values()]
      .map((entry) => ({
        position: entry.position,
        average: average(entry.values),
        count: entry.rows.length,
        rows: entry.rows,
      }))
      .sort((a, b) => (b.average || 0) - (a.average || 0));
  }, [answeredRows]);

  const trendPlayerOptions = useMemo(() => {
    return (displayedPlayers || [])
      .filter((player) => {
        if (selectedPosition !== "All" && normalizePlayerPosition(player.position) !== selectedPosition) {
          return false;
        }
        return allowedPlayerIds.has(String(player.id));
      })
      .map((player) => ({
        id: String(player.id),
        name: getPlayerDisplayName(player),
        position: normalizePlayerPosition(player.position) || "Unknown",
        profilePicture: player.profile_picture || null,
        isAtRisk: isPlayerAtRisk(player, { activeAlertPlayerIds }),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [displayedPlayers, allowedPlayerIds, selectedPosition, activeAlertPlayerIds]);

  const trendPlayerNames = useMemo(
    () => trendPlayerOptions.map((player) => player.name),
    [trendPlayerOptions]
  );

  const trendPositionTabs = useMemo(() => {
    const present = new Set(trendPlayerOptions.map((player) => player.position));
    const ordered = PLAYER_POSITION_ORDER.filter((position) => present.has(position));
    const extras = [...present]
      .filter((position) => !PLAYER_POSITION_ORDER.includes(position))
      .sort((a, b) => a.localeCompare(b));
    return [...ordered, ...extras];
  }, [trendPlayerOptions]);

  const trendColors = useMemo(
    () => buildTrendPalette(Math.max(trendPlayerNames.length, 1), isDarkMode),
    [trendPlayerNames.length, isDarkMode]
  );

  const getStyleForPlayer = (name) => {
    const index = trendPlayerNames.indexOf(name);
    const color = trendColors[index >= 0 ? index % trendColors.length : 0];
    const dash = TREND_DASHES[Math.floor(Math.max(index, 0) / trendColors.length) % TREND_DASHES.length];
    return { color, dash };
  };

  useEffect(() => {
    if (!trendPositionTabs.length) {
      setActiveTrendPosition(null);
      return;
    }
    if (!activeTrendPosition || !trendPositionTabs.includes(activeTrendPosition)) {
      setActiveTrendPosition(trendPositionTabs[0]);
    }
  }, [trendPositionTabs, activeTrendPosition]);

  const playerHistoryByName = useMemo(() => {
    const historyByName = new Map();

    (history || []).forEach((dayEntry) => {
      (dayEntry.rows || []).forEach((row) => {
        const playerId = String(row.player_id);
        if (!allowedPlayerIds.has(playerId)) return;
        if (selectedPosition !== "All" && normalizePlayerPosition(row.player_position) !== selectedPosition) {
          return;
        }

        const value = toNumber(row.rpe_value);
        if (!Number.isFinite(value)) return;

        const name = String(row.player_name || "").trim();
        if (!name) return;

        if (!historyByName.has(name)) historyByName.set(name, new Map());
        historyByName.get(name).set(dayEntry.date, value);
      });
    });

    return historyByName;
  }, [history, allowedPlayerIds, selectedPosition]);

  const { trendChartData, trendBranchData, trendDayLabels } = useMemo(() => {
    const mainPoints = [];
    const branchPoints = [];

    const byDate = new Map();
    (history || []).forEach((dayEntry) => {
      if (!dayEntry?.date) return;
      byDate.set(dayEntry.date, dayEntry);
    });

    const sortedDates = [...byDate.keys()].sort().slice(-RPE_TREND_HISTORY_DAYS);
    const trendDayLabels = sortedDates.map((date) => formatDayLabel(date));

    sortedDates.forEach((date, dayIndex) => {
      const dayEntry = byDate.get(date) || { date, sessions: [], rows: [] };
      const label = trendDayLabels[dayIndex];

      let sessionTrends = (Array.isArray(dayEntry.sessions) ? dayEntry.sessions : [])
        .map((session) => ({
          ...session,
          numericAverage: toNumber(session.average),
        }))
        .filter((session) => Number.isFinite(session.numericAverage))
        .sort((a, b) => {
          const ta = Date.parse(a.created_at ?? "") || 0;
          const tb = Date.parse(b.created_at ?? "") || 0;
          if (ta !== tb) return ta - tb;
          return String(a.session_id ?? "").localeCompare(String(b.session_id ?? ""));
        });

      const buildMainPoint = (fields) => {
        const mainPoint = {
          dayIndex,
          date,
          label,
          ...fields,
        };
        trendPlayerOptions.forEach((player) => {
          mainPoint[player.name] = playerHistoryByName.get(player.name)?.get(date) ?? null;
        });
        mainPoints.push(mainPoint);
      };

      if (sessionTrends.length > 0) {
        const lastSession = sessionTrends[sessionTrends.length - 1];
        buildMainPoint({
          sessionLabel: lastSession.label || null,
          responsesCount: lastSession.responses_count ?? null,
          averageMain: lastSession.numericAverage,
          coachReferenceMain: toNumber(lastSession.coach_rpe_value),
        });

        sessionTrends.slice(0, -1).forEach((session) => {
          branchPoints.push({
            dayIndex,
            date,
            label,
            averageBranch: session.numericAverage,
            sessionLabel: session.label || null,
            responsesCount: session.responses_count ?? null,
          });
        });
        return;
      }

      const globalValues = [];
      trendPlayerOptions.forEach((player) => {
        const value = playerHistoryByName.get(player.name)?.get(date) ?? null;
        if (Number.isFinite(value)) globalValues.push(value);
      });
      const avg = globalValues.length ? average(globalValues) : null;

      buildMainPoint({
        sessionLabel: null,
        responsesCount: globalValues.length || null,
        averageMain: Number.isFinite(avg) ? avg : null,
        coachReferenceMain: toNumber(dayEntry.coachRpeValue),
      });
    });

    return { trendChartData: mainPoints, trendBranchData: branchPoints, trendDayLabels };
  }, [history, trendPlayerOptions, playerHistoryByName]);

  const visibleTrendPlayers = useMemo(() => {
    if (!showTrendDetails) return [];

    const ids = new Set(selectedTrendPlayerIds);
    Object.entries(showAllTrendByPosition).forEach(([position, active]) => {
      if (!active) return;
      trendPlayerOptions
        .filter((player) => player.position === position)
        .forEach((player) => ids.add(player.id));
    });

    return trendPlayerOptions.filter((player) => ids.has(player.id));
  }, [showTrendDetails, selectedTrendPlayerIds, showAllTrendByPosition, trendPlayerOptions]);

  const tooltipStyle = getFeedbackTooltipStyle(isDarkMode);
  const barCursor = getFeedbackBarCursor(isDarkMode);
  const activeBar = getFeedbackActiveBar(isDarkMode);

  const detailColumns = useMemo(
    () => [
      {
        key: "playerName",
        label: t("table.player", { defaultValue: "Joueur" }),
        render: (row) => (
          <PlayerIdentityCell
            name={row.playerName}
            profilePicture={row.profilePicture}
            isDarkMode={isDarkMode}
          />
        ),
      },
      { key: "position", label: t("table.position", { defaultValue: "Poste" }) },
      {
        key: "value",
        label: t("rpe.value", { defaultValue: "Valeur RPE" }),
        className: "font-semibold tabular-nums",
        render: (row) => formatMetric(row.value),
      },
      {
        key: "previousValue",
        label: t("common.vs_prev", { defaultValue: "vs préc." }),
        className: "tabular-nums",
        render: (row) =>
          Number.isFinite(row.delta)
            ? `${row.delta > 0 ? "+" : ""}${formatMetric(row.delta)}`
            : "-",
      },
    ],
    [t, isDarkMode]
  );

  const toDetailRows = (detailRows) =>
    [...(detailRows || [])]
      .filter((row) => Number.isFinite(row.numericValue))
      .sort((a, b) => b.numericValue - a.numericValue || String(a.player_name).localeCompare(String(b.player_name)))
      .map((row) => {
        const playerId = String(row.player_id ?? "");
        return {
          id: row.player_id,
          playerName: row.player_name,
          profilePicture: playerProfileById.get(playerId) || null,
          position: row.player_position || t("common.na", { defaultValue: "N/A" }),
          value: row.numericValue,
          previousValue: row.numericPrevValue,
          delta:
            Number.isFinite(row.numericValue) && Number.isFinite(row.numericPrevValue)
              ? row.numericValue - row.numericPrevValue
              : null,
        };
      });

  const openDetail = ({ title, subtitle, rows: detailRows }) => {
    setDetailModal({
      title,
      subtitle,
      rows: toDetailRows(detailRows),
    });
  };

  const handleTrendPlayerToggle = (playerId) => {
    const player = trendPlayerOptions.find((entry) => entry.id === playerId);
    if (!player) return;

    if (showAllTrendByPosition[player.position]) {
      setShowAllTrendByPosition((previous) => ({ ...previous, [player.position]: false }));
      setSelectedTrendPlayerIds(new Set([playerId]));
      return;
    }

    setSelectedTrendPlayerIds((previous) => {
      const next = new Set(previous);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  };

  const handleTrendShowAll = (position) => {
    setShowAllTrendByPosition((previous) => {
      const nextActive = !previous[position];
      if (nextActive) {
        setSelectedTrendPlayerIds((ids) => {
          const next = new Set(ids);
          trendPlayerOptions
            .filter((player) => player.position === position)
            .forEach((player) => next.delete(player.id));
          return next;
        });
      }
      return { ...previous, [position]: nextActive };
    });
  };

  const averageTrendLabel = t("feedback.kpi_average", { defaultValue: "Moyenne" });
  const coachTrendLabel = t("feedback.coach_rpe_reference", { defaultValue: "RPE coach" });
  const branchTrendLabel = t("feedback.rpe_trend_branch_session");
  const coachReferenceBucket = Number.isFinite(numericCoachRpeValue)
    ? String(Math.max(1, Math.min(10, Math.round(numericCoachRpeValue))))
    : null;
  const isPastSportDay = Boolean(selectedDate) && selectedDate !== currentRpeSportDayIso();

  const selectedSession = useMemo(() => {
    if (!selectedSessionId || !Array.isArray(sessions)) return null;
    return sessions.find((session) => String(session?.session_id) === String(selectedSessionId)) || null;
  }, [sessions, selectedSessionId]);

  const selectedSessionLabel = useMemo(() => {
    if (!selectedSessionId || !Array.isArray(sessions)) return "";
    const index = sessions.findIndex((session) => String(session?.session_id) === String(selectedSessionId));
    if (index < 0) return "";
    const session = sessions[index];
    return typeof formatSessionLabel === "function"
      ? formatSessionLabel(session, index)
      : `Session ${index + 1}`;
  }, [sessions, selectedSessionId, formatSessionLabel]);

  const exportSessions = useMemo(
    () =>
      (sessions || []).map((session, index) => ({
        label:
          typeof formatSessionLabel === "function"
            ? formatSessionLabel(session, index)
            : `Session ${index + 1}`,
        average: toNumber(session?.average),
        responsesCount: session?.responses_count ?? null,
        coachRpe: toNumber(session?.coach_rpe_value),
        createdAt: session?.created_at ?? "",
        isOpen: Boolean(session?.is_open || !session?.closed_at),
      })),
    [sessions, formatSessionLabel]
  );

  const trendPlayersExportRows = useMemo(() => {
    const dates = [...new Set((history || []).map((entry) => entry?.date).filter(Boolean))]
      .sort()
      .slice(-RPE_TREND_HISTORY_DAYS);

    const rows = [];
    dates.forEach((date) => {
      trendPlayerOptions.forEach((player) => {
        const rpe = playerHistoryByName.get(player.name)?.get(date) ?? null;
        if (!Number.isFinite(rpe)) return;
        rows.push({
          date,
          playerName: player.name,
          position: player.position,
          rpe,
        });
      });
    });
    return rows;
  }, [history, trendPlayerOptions, playerHistoryByName]);

  const rpeExportData = useMemo(
    () => ({
      selectedDate,
      selectedPosition,
      selectedSessionLabel,
      coachRpeValue: numericCoachRpeValue,
      summary: {
        average: averageValue,
        median: medianValue,
        min: minValue,
        max: maxValue,
        completionRate,
        answerCount,
        totalPlayers: filteredRows.length,
        coachVsAverageDelta,
        averageDelta,
      },
      players: filteredRows.map((row) => ({
        name: row.player_name,
        position: row.player_position,
        rpe: row.numericValue,
        previousRpe: row.numericPrevValue,
        delta:
          Number.isFinite(row.numericValue) && Number.isFinite(row.numericPrevValue)
            ? row.numericValue - row.numericPrevValue
            : null,
        date: row.date,
      })),
      byPosition: byPositionData,
      distribution: distributionData.map((entry) => ({
        bucket: entry.bucket,
        count: entry.count,
      })),
      sessions: exportSessions,
      trend: {
        main: trendChartData,
        branch: trendBranchData,
      },
      trendPlayers: trendPlayersExportRows,
    }),
    [
      selectedDate,
      selectedPosition,
      selectedSessionLabel,
      numericCoachRpeValue,
      averageValue,
      medianValue,
      minValue,
      maxValue,
      completionRate,
      answerCount,
      filteredRows,
      coachVsAverageDelta,
      averageDelta,
      byPositionData,
      distributionData,
      exportSessions,
      trendChartData,
      trendBranchData,
      trendPlayersExportRows,
    ]
  );

  useEffect(() => {
    if (!trendExpanded) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setTrendExpanded(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [trendExpanded]);

  return (
    <div className="space-y-4">
      <div
        className={`rounded-2xl border p-4 ${
          isDarkMode ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50"
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className={`text-xs uppercase tracking-wide ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}>
              {t("feedback.rpe_title", { defaultValue: "RPE Feedback" })}
            </p>
            <h3 className="mt-1 text-xl font-semibold">
              {t("feedback.overview_title", { defaultValue: "Overview des resultats" })}
            </h3>
            <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              {t("feedback.rpe_overview_subtitle", {
                defaultValue: "Suivi de la charge percue, tendances et comparaisons par poste.",
              })}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <RpeOverviewExportMenu
              data={rpeExportData}
              isDarkMode={isDarkMode}
              disabled={!selectedDate}
            />
            {canEditInputs && typeof onRequestEdit === "function" && (
              <button
                type="button"
                onClick={onRequestEdit}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isDarkMode
                    ? "bg-amber-500 text-black hover:bg-amber-400"
                    : "bg-amber-500 text-white hover:bg-amber-600"
                }`}
              >
                {t("feedback.edit_entry", { defaultValue: "Modification de la saisie" })}
              </button>
            )}
          </div>
        </div>

        <RpeAutoCloseSettings isDarkMode={isDarkMode} />

        {Array.isArray(sessions) && sessions.length > 0 && typeof onSelectSession === "function" && (
          <div className="mt-4 border-t border-amber-500/20 pt-4">
            <p className={`text-xs uppercase tracking-wide ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}>
              {t("rpe.sessions_for_day", { defaultValue: "Sessions for this day" })}
            </p>
            <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              {t("rpe.sessions_for_day_hint", {
                defaultValue: "Select a session to load its overview, then switch to edit mode to update values.",
              })}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {sessions.map((session, index) => {
                const sessionId = String(session?.session_id ?? "");
                const isSelected = selectedSessionId && String(selectedSessionId) === sessionId;
                const isOpen = session?.is_open || !session?.closed_at;
                const label = typeof formatSessionLabel === "function"
                  ? formatSessionLabel(session, index)
                  : `Session ${index + 1}`;
                return (
                  <button
                    key={sessionId || index}
                    type="button"
                    onClick={() => onSelectSession(sessionId)}
                    className={`inline-flex max-w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-xs transition ${
                      isSelected
                        ? isDarkMode
                          ? "border-amber-400 bg-amber-500/20 text-amber-100 ring-1 ring-amber-400/40"
                          : "border-amber-400 bg-white text-amber-900 ring-1 ring-amber-200"
                        : isDarkMode
                        ? "border-gray-600 bg-gray-900/60 text-gray-200 hover:border-amber-400/50 hover:bg-gray-800"
                        : "border-gray-200 bg-white text-gray-700 hover:border-amber-300 hover:bg-amber-50/60"
                    }`}
                    title={t("rpe.select_session_action", { defaultValue: "Select this session" })}
                  >
                    <span className="truncate font-medium">{label}</span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        isOpen
                          ? isDarkMode
                            ? "bg-emerald-500/20 text-emerald-200"
                            : "bg-emerald-50 text-emerald-700"
                          : isDarkMode
                          ? "bg-gray-600/50 text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {isOpen
                        ? t("rpe.session_open", { defaultValue: "Open" })
                        : t("rpe.session_closed", { defaultValue: "Closed" })}
                    </span>
                  </button>
                );
              })}
            </div>
            {selectedSession && typeof onSessionMutated === "function" && (
              <div className="mt-3 space-y-2">
                <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {t("rpe.session_actions_hint")}
                </p>
                <RpeSessionManageBar
                  session={selectedSession}
                  clubId={clubId}
                  isDarkMode={isDarkMode}
                  targetDate={selectedDate}
                  onMutated={onSessionMutated}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {!hasOverviewData ? (
        <>
        <FeedbackInsufficientDataPanel
          isDarkMode={isDarkMode}
          variant="amber"
          layout="hero"
          answerCount={answerCount}
          minRequired={effectiveMinRequired}
          previousDates={previousReadyDates}
          onSelectDate={onSelectDate}
          title={t("feedback.rpe_insufficient_title", {
            defaultValue: "RPE questionnaire not yet completed",
          })}
          message={t("feedback.rpe_insufficient_message", {
            defaultValue: "Players have not yet answered the RPE questionnaire for this day.",
          })}
        />
        {isPastSportDay && typeof onRequestBackfill === "function" && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onRequestBackfill}
              className={`rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                isDarkMode
                  ? "bg-indigo-500/90 text-white hover:bg-indigo-400"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"
              }`}
            >
              {t("rpe.create_backfill_from_overview")}
            </button>
          </div>
        )}
        </>
      ) : (
        <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.coach_rpe_reference", { defaultValue: "RPE coach" })}
          value={Number.isFinite(numericCoachRpeValue) ? formatMetric(numericCoachRpeValue) : "-"}
          hint={
            coachVsAverageDelta === null
              ? t("feedback.coach_rpe_reference_hint", {
                  defaultValue: "Note de reference definie par le coach pour la session.",
                })
              : t("feedback.coach_vs_team_average", {
                  defaultValue: "vs moyenne equipe: {{value}}",
                  value: `${coachVsAverageDelta > 0 ? "+" : ""}${formatMetric(coachVsAverageDelta)}`,
                })
          }
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.kpi_average", { defaultValue: "Moyenne" })}
          value={formatMetric(averageValue)}
          onClick={() =>
            openDetail({
              title: t("feedback.kpi_average", { defaultValue: "Moyenne" }),
              subtitle: t("feedback.kpi_answers", {
                defaultValue: "{{count}} reponses",
                count: answeredRows.length,
              }),
              rows: answeredRows,
            })
          }
          hint={
            coachVsAverageDelta !== null
              ? t("feedback.vs_coach_reference", {
                  defaultValue: "vs coach: {{value}}",
                  value: `${coachVsAverageDelta > 0 ? "+" : ""}${formatMetric(coachVsAverageDelta)}`,
                })
              : averageDelta === null
              ? t("feedback.no_comparison", { defaultValue: "Pas de comparaison disponible" })
              : t("feedback.vs_previous_day", {
                  defaultValue: "vs veille: {{value}}",
                  value: `${averageDelta > 0 ? "+" : ""}${formatMetric(averageDelta)}`,
                })
          }
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.kpi_median", { defaultValue: "Mediane" })}
          value={formatMetric(medianValue)}
          onClick={() =>
            openDetail({
              title: t("feedback.kpi_median", { defaultValue: "Mediane" }),
              subtitle: t("feedback.kpi_min_max", { defaultValue: "Min/Max" }),
              rows: answeredRows,
            })
          }
          hint={
            minValue === null || maxValue === null
              ? null
              : `${t("feedback.kpi_min_max", { defaultValue: "Min/Max" })}: ${formatMetric(minValue)} / ${formatMetric(maxValue)}`
          }
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.kpi_completion", { defaultValue: "Taux de reponse" })}
          value={`${formatMetric(completionRate, 0)}%`}
          onClick={() =>
            openDetail({
              title: t("feedback.kpi_completion", { defaultValue: "Taux de reponse" }),
              subtitle: t("feedback.kpi_answers", {
                defaultValue: "{{count}} reponses",
                count: answeredRows.length,
              }),
              rows: answeredRows,
            })
          }
          hint={t("feedback.kpi_answers", {
            defaultValue: "{{count}} reponses",
            count: answeredRows.length,
          })}
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.kpi_players", { defaultValue: "Joueurs analyses" })}
          value={String(filteredRows.length)}
          onClick={() =>
            openDetail({
              title: t("feedback.kpi_players", { defaultValue: "Joueurs analyses" }),
              subtitle: selectedPosition === "All" ? t("feedback.all_positions", { defaultValue: "Tous les postes" }) : selectedPosition,
              rows: answeredRows,
            })
          }
          hint={
            selectedPosition === "All"
              ? t("feedback.all_positions", { defaultValue: "Tous les postes" })
              : selectedPosition
          }
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          isDarkMode={isDarkMode}
          title={t("feedback.rpe_trend_title", { defaultValue: "Tendance RPE" })}
          subtitle={
            historyLoading
              ? t("feedback.loading_history", { defaultValue: "Chargement de l'historique..." })
              : showTrendDetails
              ? t("feedback.rpe_trend_subtitle_details", {
                  defaultValue: "Courbes individuelles et moyenne globale sur les derniers jours.",
                })
              : t("feedback.rpe_trend_subtitle", {
                  defaultValue: "Moyenne globale sur les derniers jours.",
                })
          }
        >
          {trendChartData.length === 0 ? (
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("feedback.no_trend_data", { defaultValue: "Pas assez de donnees pour afficher une tendance." })}
            </p>
          ) : (
            <div className={`grid gap-4 ${showTrendDetails ? "xl:grid-cols-2" : ""}`}>
              <div className="min-w-0">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("feedback.rpe_trend_last_days", { count: RPE_TREND_HISTORY_DAYS })}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <TrendDetailsModeToggle
                      isDarkMode={isDarkMode}
                      showDetails={showTrendDetails}
                      onChange={setShowTrendDetails}
                      t={t}
                    />
                    <button
                      type="button"
                      onClick={() => setTrendExpanded(true)}
                      title={t("feedback.rpe_trend_expand", { defaultValue: "Agrandir le graphique" })}
                      aria-label={t("feedback.rpe_trend_expand", { defaultValue: "Agrandir le graphique" })}
                      className={`inline-flex items-center justify-center rounded-xl border p-2 transition-colors ${
                        isDarkMode
                          ? "border-gray-600/80 bg-gray-900/70 text-gray-300 hover:bg-gray-800 hover:text-amber-200"
                          : "border-gray-200 bg-slate-100/90 text-gray-600 hover:bg-white hover:text-amber-700"
                      }`}
                    >
                      <Maximize2 className="h-4 w-4" strokeWidth={2.25} />
                    </button>
                  </div>
                </div>

                <RpeTrendChart
                  trendChartData={trendChartData}
                  trendBranchData={trendBranchData}
                  trendDayLabels={trendDayLabels}
                  showTrendDetails={showTrendDetails}
                  visibleTrendPlayers={visibleTrendPlayers}
                  getStyleForPlayer={getStyleForPlayer}
                  isDarkMode={isDarkMode}
                  t={t}
                  averageTrendLabel={averageTrendLabel}
                  coachTrendLabel={coachTrendLabel}
                  branchTrendLabel={branchTrendLabel}
                />
              </div>

              {showTrendDetails ? (
                <div
                  className={`flex h-64 min-h-64 flex-col rounded-xl border p-3 ${
                    isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-slate-50/80"
                  }`}
                >
                  <p className={`mb-2 shrink-0 text-xs font-medium uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("feedback.rpe_trend_player_filter", { defaultValue: "Joueurs" })}
                  </p>
                  {trendPlayerOptions.length === 0 || !activeTrendPosition ? (
                    <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      {t("feedback.no_data", { defaultValue: "Aucune donnée disponible" })}
                    </p>
                  ) : (
                    <RpeTrendPlayerSelector
                      players={trendPlayerOptions}
                      positionTabs={trendPositionTabs}
                      activePosition={activeTrendPosition}
                      onPositionChange={setActiveTrendPosition}
                      selectedPlayerIds={selectedTrendPlayerIds}
                      showAllByPosition={showAllTrendByPosition}
                      onTogglePlayer={handleTrendPlayerToggle}
                      onToggleShowAll={handleTrendShowAll}
                      getStyleForPlayer={getStyleForPlayer}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </div>
              ) : null}
            </div>
          )}
        </Panel>

        <Panel
          isDarkMode={isDarkMode}
          title={t("feedback.rpe_distribution_title", { defaultValue: "Distribution du jour" })}
          subtitle={t("feedback.rpe_distribution_subtitle", {
            defaultValue: "Repartition des notes RPE (arrondi).",
          })}
        >
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distributionData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                <XAxis dataKey="bucket" tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle} cursor={barCursor} />
                {coachReferenceBucket ? (
                  <ReferenceLine
                    x={coachReferenceBucket}
                    stroke="#6366f1"
                    strokeDasharray="4 4"
                    strokeWidth={2}
                    label={{
                      value: coachTrendLabel,
                      fill: isDarkMode ? "#a5b4fc" : "#4338ca",
                      fontSize: 11,
                      position: "insideTopRight",
                    }}
                  />
                ) : null}
                <Bar
                  dataKey="count"
                  fill="#f97316"
                  radius={[6, 6, 0, 0]}
                  activeBar={activeBar}
                  className="cursor-pointer"
                  onClick={(entry) => {
                    const bucket = entry?.payload;
                    openDetail({
                      title: `RPE ${bucket?.bucket ?? ""}`,
                      subtitle: t("feedback.kpi_answers", {
                        defaultValue: "{{count}} reponses",
                        count: bucket?.count || 0,
                      }),
                      rows: bucket?.rows || [],
                    });
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          isDarkMode={isDarkMode}
          title={t("feedback.rpe_position_compare", { defaultValue: "Comparaison par poste" })}
          subtitle={t("feedback.rpe_position_compare_subtitle", {
            defaultValue: "Moyenne RPE par poste pour les joueurs visibles.",
          })}
        >
          {byPositionData.length === 0 ? (
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("feedback.no_data", { defaultValue: "Aucune donnee disponible" })}
            </p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byPositionData} layout="vertical" margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                  <XAxis type="number" domain={[0, 10]} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="position"
                    width={80}
                    tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                  />
                  <Tooltip contentStyle={tooltipStyle} cursor={barCursor} formatter={(value) => formatMetric(value)} />
                  {Number.isFinite(numericCoachRpeValue) ? (
                    <ReferenceLine
                      x={numericCoachRpeValue}
                      stroke="#6366f1"
                      strokeDasharray="4 4"
                      strokeWidth={2}
                      label={{
                        value: coachTrendLabel,
                        fill: isDarkMode ? "#a5b4fc" : "#4338ca",
                        fontSize: 11,
                        position: "insideTopRight",
                      }}
                    />
                  ) : null}
                  <Bar
                    dataKey="average"
                    fill="#fb923c"
                    radius={[0, 6, 6, 0]}
                    activeBar={activeBar}
                    className="cursor-pointer"
                    onClick={(entry) => {
                      const position = entry?.payload;
                      openDetail({
                        title: position?.position || t("common.position", { defaultValue: "Poste" }),
                        subtitle: `${formatMetric(position?.average)} RPE - ${t("feedback.kpi_answers", {
                          defaultValue: "{{count}} reponses",
                          count: position?.count || 0,
                        })}`,
                        rows: position?.rows || [],
                      });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel
          isDarkMode={isDarkMode}
          title={t("feedback.rpe_movers_title", { defaultValue: "Variations marquantes" })}
          subtitle={t("feedback.rpe_movers_subtitle", {
            defaultValue: "Evolution individuelle vs valeur precedente.",
          })}
        >
          {biggestMovers.length === 0 ? (
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("feedback.no_comparison", { defaultValue: "Pas de comparaison disponible" })}
            </p>
          ) : (
            <ul className="space-y-2">
              {biggestMovers.map((row) => (
                <li
                  key={row.player_id}
                  onClick={() =>
                    openDetail({
                      title: row.player_name,
                      subtitle: t("feedback.rpe_movers_subtitle", {
                        defaultValue: "Evolution individuelle vs valeur precedente.",
                      }),
                      rows: [row],
                    })
                  }
                  className={`flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 transition-colors ${
                    isDarkMode ? "bg-gray-900/60 hover:bg-gray-700/40" : "bg-gray-50 hover:bg-slate-100"
                  }`}
                >
                  <span className="text-sm font-medium">{row.player_name}</span>
                  <span
                    className={`text-sm font-semibold ${
                      row.delta > 0
                        ? "text-rose-500"
                        : row.delta < 0
                        ? "text-emerald-500"
                        : isDarkMode
                        ? "text-gray-300"
                        : "text-gray-700"
                    }`}
                  >
                    {row.delta > 0 ? "+" : ""}
                    {formatMetric(row.delta)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
        </>
      )}
      {trendExpanded && trendChartData.length > 0 ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <button
            type="button"
            aria-label={t("common.close", { defaultValue: "Fermer" })}
            onClick={() => setTrendExpanded(false)}
            className="absolute inset-0 bg-black/55"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="rpe-trend-expanded-title"
            className={`relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border shadow-2xl ${
              isDarkMode ? "border-gray-700 bg-gray-900 text-gray-100" : "border-gray-200 bg-white text-gray-900"
            }`}
          >
            <div className={`border-b px-5 py-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 id="rpe-trend-expanded-title" className="text-lg font-semibold">
                    {t("feedback.rpe_trend_expand_title", { defaultValue: "Tendance RPE — vue détaillée" })}
                  </h3>
                  <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {showTrendDetails
                      ? t("feedback.rpe_trend_subtitle_details")
                      : t("feedback.rpe_trend_subtitle")}
                  </p>
                  <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    {t("feedback.rpe_trend_zoom_hint", {
                      defaultValue: "Faites glisser la barre sous le graphique pour zoomer sur une période.",
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setTrendExpanded(false)}
                  aria-label={t("common.close", { defaultValue: "Fermer" })}
                  className={`rounded-lg p-2 transition-colors ${
                    isDarkMode ? "text-gray-400 hover:bg-gray-800 hover:text-gray-200" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {t("feedback.rpe_trend_last_days", { count: RPE_TREND_HISTORY_DAYS })}
                </p>
                <TrendDetailsModeToggle
                  isDarkMode={isDarkMode}
                  showDetails={showTrendDetails}
                  onChange={setShowTrendDetails}
                  t={t}
                />
              </div>
              <div className={`grid gap-4 ${showTrendDetails ? "xl:grid-cols-[minmax(0,1fr)_280px]" : ""}`}>
                <RpeTrendChart
                  trendChartData={trendChartData}
                  trendBranchData={trendBranchData}
                  trendDayLabels={trendDayLabels}
                  showTrendDetails={showTrendDetails}
                  visibleTrendPlayers={visibleTrendPlayers}
                  getStyleForPlayer={getStyleForPlayer}
                  isDarkMode={isDarkMode}
                  expanded
                  showBrush
                  t={t}
                  averageTrendLabel={averageTrendLabel}
                  coachTrendLabel={coachTrendLabel}
                  branchTrendLabel={branchTrendLabel}
                  containerClassName="h-[min(520px,58vh)] min-h-[360px]"
                />
                {showTrendDetails ? (
                  <div
                    className={`flex min-h-[280px] flex-col rounded-xl border p-3 xl:min-h-0 xl:h-[min(520px,58vh)] ${
                      isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-slate-50/80"
                    }`}
                  >
                    <p className={`mb-2 shrink-0 text-xs font-medium uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                      {t("feedback.rpe_trend_player_filter", { defaultValue: "Joueurs" })}
                    </p>
                    {trendPlayerOptions.length === 0 || !activeTrendPosition ? (
                      <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        {t("feedback.no_data", { defaultValue: "Aucune donnée disponible" })}
                      </p>
                    ) : (
                      <RpeTrendPlayerSelector
                        players={trendPlayerOptions}
                        positionTabs={trendPositionTabs}
                        activePosition={activeTrendPosition}
                        onPositionChange={setActiveTrendPosition}
                        selectedPlayerIds={selectedTrendPlayerIds}
                        showAllByPosition={showAllTrendByPosition}
                        onTogglePlayer={handleTrendPlayerToggle}
                        onToggleShowAll={handleTrendShowAll}
                        getStyleForPlayer={getStyleForPlayer}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <FeedbackDetailModal
        isOpen={Boolean(detailModal)}
        onClose={() => setDetailModal(null)}
        isDarkMode={isDarkMode}
        title={detailModal?.title}
        subtitle={detailModal?.subtitle}
        rows={detailModal?.rows || []}
        columns={detailColumns}
        emptyLabel={t("feedback.no_data", { defaultValue: "Aucune donnée disponible" })}
        closeLabel={t("common.close", { defaultValue: "Fermer" })}
      />
    </div>
  );
};

export default RpeFeedbackOverview;
